/** Add / Edit Link on a project — port of the original linkModal(). `index` = the link being edited. */
import { useState } from 'react';
import type { Project, ProjectLink } from '@/types/models';
import { cleanUrl } from '@/domain/format';
import { LIMITS, validateLink, type FieldErrors } from '@/domain/validation';
import { useDataset } from '@/hooks/data';
import { ErrorState, Field, LoadingState, ModalFrame } from '@/ui/kit';
import { useProjectPatch } from './useProjectPatch';

export function LinkModal({ projectId, index, onClose }: { projectId: string; index?: number; onClose: () => void }) {
  const ds = useDataset();
  const p = ds.data?.projects.find((x) => x.id === projectId);
  const isEdit = index != null;
  const title = `${isEdit ? 'Edit' : 'Add'} Link`;
  if (ds.error) return <ModalFrame title={title} onClose={onClose}><ErrorState error={ds.error} onRetry={ds.refetch} /></ModalFrame>;
  if (!ds.data) return <ModalFrame title={title} onClose={onClose}><LoadingState /></ModalFrame>;
  if (!p || (isEdit && !p.links[index])) return <ModalFrame title={title} onClose={onClose}><div className="empty">No records found.</div></ModalFrame>;
  return <LinkForm key={`${p.id}-${index ?? 'new'}`} project={p} index={index} title={title} onClose={onClose} />;
}

function LinkForm({ project: p, index, title, onClose }: { project: Project; index?: number; title: string; onClose: () => void }) {
  const isEdit = index != null;
  const [f, setF] = useState<ProjectLink>(isEdit ? { ...p.links[index] } : { label: '', url: '' });
  const [err, setErr] = useState<FieldErrors>({});
  const save = useProjectPatch(p.id);
  const submit = async () => {
    const e = validateLink(f);
    setErr(e);
    if (Object.keys(e).length) return;
    const o: ProjectLink = { label: f.label.trim(), url: cleanUrl(f.url) };
    const links = isEdit ? p.links.map((l, i) => (i === index ? o : l)) : [...p.links, o];
    const ok = await save.run({ links }, isEdit ? 'Link updated successfully.' : 'Link added successfully.');
    if (ok) onClose();
  };
  return (
    <form onSubmit={(e) => (e.preventDefault(), void submit())} noValidate>
      <ModalFrame
        title={title}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={save.pending}>
              {isEdit ? 'Update' : 'Add'}
            </button>
          </>
        }
      >
        <Field label="Label" required error={err.label}>
          <input name="label" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="e.g. Admin panel, Results sheet" maxLength={LIMITS.linkLabel} />
        </Field>
        <Field label="URL" required error={err.url}>
          <input name="url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://" maxLength={LIMITS.linkUrl} />
        </Field>
      </ModalFrame>
    </form>
  );
}
